import React, { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import { useCoarsePointer } from '../../hooks/useCoarsePointer';
import {
  DATE_PLACEHOLDER,
  applyDateMask,
  displayToIso,
  isoToDisplay,
  segmentRangeAt,
  stepSegment,
} from './dateMask';
import './DateField.scss';

interface DateFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'value'
> {
  /** ISO `yyyy-mm-dd`, or '' for empty. Unchanged from when this wrapped a native date input. */
  value: string;
  onChange: (value: string) => void;
  /** Shows a × that empties the field. */
  clearable?: boolean;
  clearTitle?: string;
}

/**
 * The app's date input: a text box masked to `mm/dd/yyyy`, with the native
 * calendar still available behind the icon.
 *
 * ## Why not `<input type="date">`
 *
 * Admins wanted the click behaviour every other text field has — click to place
 * the caret, double-click to select a part, triple-click to select the lot.
 * `type="date"` is excluded from the `setSelectionRange`/`select()` APIs (they
 * throw), so on a native date input that is impossible; the browser owns the
 * selection entirely. Getting it means owning the text.
 *
 * ## What is kept, so this isn't a downgrade
 *
 * - `value`/`onChange` still speak ISO `yyyy-mm-dd`, so nothing upstream changed
 * - the calendar picker still opens, from a hidden native input that keeps
 *   honouring `min`/`max` exactly as before
 * - arrow keys still step the segment under the caret
 *
 * ## On touch, this hands back to the platform
 *
 * Every reason above is a mouse-and-keyboard reason. A finger has no caret, no
 * double-click and no hover, so on a coarse pointer the masked box buys nothing
 * and costs the one thing that matters: the hidden proxy input is 1×1px and
 * transparent, and no mobile browser will anchor a picker to that. `showPicker`
 * is also Safari 16.4+, and the older fallback — focusing the proxy — cannot
 * open an iOS wheel at all, so the button was a silent no-op.
 *
 * So a coarse pointer gets the real `<input type="date">`, tapped directly.
 * **Nothing in that branch may call `setSelectionRange` or `select()`** — those
 * throw on a date input, which is what drove the masked box in the first place.
 *
 * The display format is fixed `mm/dd/yyyy` rather than locale-derived. That
 * matches `formatDate` everywhere else in the app; it would need revisiting for
 * a non-US locale. The native branch shows whatever the platform shows.
 */
export function DateField({
  value,
  onChange,
  clearable = false,
  clearTitle = 'Clear date',
  className,
  disabled,
  min,
  max,
  onKeyDown,
  onClick,
  onBlur,
  ...rest
}: DateFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLInputElement>(null);
  const coarsePointer = useCoarsePointer();

  const [text, setText] = useState(() => isoToDisplay(value));

  // Follow the prop whenever it names a different day than what is on screen —
  // "Copy pickup date" and resetting the form both come through this way. The
  // comparison is on the parsed value, so a half-typed date isn't yanked away
  // mid-keystroke.
  useEffect(() => {
    if (displayToIso(text) !== value) {
      setText(isoToDisplay(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  /** Re-select a segment after we rewrite the text, since React resets the caret. */
  const selectRange = (start: number, end: number) => {
    requestAnimationFrame(() => inputRef.current?.setSelectionRange(start, end));
  };

  const commit = (next: string) => {
    setText(next);

    const iso = displayToIso(next);
    if (iso) {
      onChange(iso);
    } else if (next === '') {
      onChange('');
    }
    // A partial date emits nothing — the parent keeps the last good value until
    // the field is complete, or blur puts it back.
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    commit(applyDateMask(e.target.value));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;

    const input = e.currentTarget;
    const caret = input.selectionStart ?? 0;

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const stepped = stepSegment(text, caret, e.key === 'ArrowUp' ? 1 : -1);
      if (!stepped) return;

      e.preventDefault();
      commit(stepped);

      const [start, end] = segmentRangeAt(stepped, caret);
      selectRange(start, end);
      return;
    }

    // Backspacing onto a separator should eat the digit before it, otherwise the
    // caret just sits there and deleting appears to do nothing.
    if (e.key === 'Backspace' && caret > 0 && caret === input.selectionEnd) {
      if (text[caret - 1] === '/') {
        e.preventDefault();
        const digits = text.slice(0, caret - 2) + text.slice(caret);
        commit(applyDateMask(digits));
        selectRange(caret - 2, caret - 2);
      }
    }
  };

  /**
   * The reason this component exists. `detail` counts clicks in the sequence, so
   * one handler covers all three: leave the caret alone, select the segment, or
   * select the whole date.
   */
  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    onClick?.(e);

    const input = e.currentTarget;

    if (e.detail >= 3) {
      input.setSelectionRange(0, text.length);
      return;
    }

    if (e.detail === 2) {
      const [start, end] = segmentRangeAt(text, input.selectionStart ?? 0);
      input.setSelectionRange(start, end);
    }
  };

  // An incomplete entry can't be stored, so put back whatever the parent holds.
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
    if (displayToIso(text) === null && text !== '') {
      setText(isoToDisplay(value));
    }
  };

  const openPicker = () => {
    const picker = pickerRef.current;
    if (!picker) return;

    // showPicker landed in Chrome 99 / Firefox 101 / Safari 16; older browsers
    // fall back to focusing the native input, which still opens on Enter.
    if (typeof picker.showPicker === 'function') {
      try {
        picker.showPicker();
        return;
      } catch {
        // Refused — no user activation, or a browser that will not present a
        // picker for a hidden input. Focusing is better than throwing.
      }
    }

    picker.focus();
  };

  const showClear = clearable && Boolean(coarsePointer ? value : text) && !disabled;

  if (coarsePointer) {
    return (
      <div className={`date-field date-field--native${showClear ? ' date-field--clearable' : ''}`}>
        <input
          {...rest}
          type="date"
          className={className}
          value={value}
          min={min}
          max={max}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onClick={onClick}
          onBlur={onBlur}
        />

        {showClear && (
          <button
            type="button"
            className="date-field__clear"
            onClick={() => onChange('')}
            title={clearTitle}
            aria-label={clearTitle}
          >
            ×
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`date-field${showClear ? ' date-field--clearable' : ''}`}>
      <input
        {...rest}
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={DATE_PLACEHOLDER}
        className={className}
        value={text}
        disabled={disabled}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        onBlur={handleBlur}
      />

      {showClear && (
        <button
          type="button"
          className="date-field__clear"
          onClick={() => commit('')}
          title={clearTitle}
          aria-label={clearTitle}
        >
          ×
        </button>
      )}

      <button
        type="button"
        className="date-field__picker-button"
        onClick={openPicker}
        disabled={disabled}
        title="Open calendar"
        aria-label="Open calendar"
        tabIndex={-1}
      >
        <Calendar size={14} />
      </button>

      {/*
        Rendered, but invisible: showPicker() refuses to open for an element that
        isn't being rendered, so this can't be display:none. It keeps min/max so
        the calendar greys out the same days it always did.
      */}
      <input
        ref={pickerRef}
        type="date"
        className="date-field__picker"
        data-testid="date-field-picker"
        tabIndex={-1}
        aria-hidden="true"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => commit(isoToDisplay(e.target.value))}
      />
    </div>
  );
}

export default DateField;
